// 🔄 Workflow 탭 - 쓰기 검증 (테스트 컬렉션)
// [배경] Preflight → Write → Post-verify 워크플로우 테스트

import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Collapse
} from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  PlayArrow as RunIcon,
  CheckCircle as PassIcon,
  Cancel as FailIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  CleaningServices as CleanupIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';

import {
  SSOTTestService,
  WorkflowTestResult,
  LookupStepLog,
  TestRunContext
} from '../../../../services/ssotTestService';
import { DeviceType, DeviceBrand, ConnectivityType } from '../../../../types/deviceCatalog';
import { GradePriceInfo } from '../../../../types/gradeSystem';

interface WorkflowTabProps {
  testRun: TestRunContext | null;
  onWorkflowComplete: (results: WorkflowTestResult[]) => void;
  workflowResults: WorkflowTestResult[];
}

/**
 * 🔄 Workflow 탭 컴포넌트
 * - A) 신규 모델 추가 테스트
 * - B) 가격 추가 테스트
 * - C) 가격 수정 테스트
 * - D) 가격 삭제 테스트
 * - E) Cleanup
 */
const WorkflowTab: React.FC<WorkflowTabProps> = ({
  testRun,
  onWorkflowComplete,
  workflowResults
}) => {
  // === 상태 ===
  const [loading, setLoading] = useState(false);
  const [expandedPanel, setExpandedPanel] = useState<string | false>('addModel');
  const [showLogs, setShowLogs] = useState<Record<number, boolean>>({});
  
  // 모델 추가 폼
  const [modelForm, setModelForm] = useState({
    brand: 'Apple' as DeviceBrand,
    deviceType: 'smartphone' as DeviceType,
    series: 'iPhone 16',
    modelEn: 'iPhone 16 Pro',
    modelKo: '아이폰 16 프로',
    storageOptions: '128GB,256GB,512GB',
    connectivityOptions: 'Wi-Fi'
  });
  
  // 가격 수정 폼
  const [priceEditForm, setPriceEditForm] = useState({
    priceId: '',
    gradeA: 100000,
    gradeB: 80000,
    gradeC: 60000,
    gradeD: 40000,
    gradeE: 20000
  });
  
  // 가격 삭제 폼
  const [deleteForm, setDeleteForm] = useState({
    modelId: '',
    storage: '128GB',
    connectivity: 'Wi-Fi'
  });
  
  // 마지막 생성된 모델 ID (가격 테스트용)
  const [lastCreatedModelId, setLastCreatedModelId] = useState<string>('');
  
  // === 패널 토글 ===
  const handlePanelChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedPanel(isExpanded ? panel : false);
  };
  
  // === A) 신규 모델 추가 테스트 ===
  const handleTestAddModel = useCallback(async () => {
    if (!testRun) return;
    
    setLoading(true);
    try {
      const storageOptions = modelForm.storageOptions.split(',').map(s => s.trim());
      const connectivityOptions = modelForm.connectivityOptions
        ? modelForm.connectivityOptions.split(',').map(s => s.trim()) as ConnectivityType[]
        : undefined;
      
      const result = await SSOTTestService.testAddModel(
        modelForm.brand,
        modelForm.deviceType,
        modelForm.series,
        modelForm.modelEn,
        modelForm.modelKo,
        storageOptions,
        connectivityOptions
      );
      
      if (result.passed && result.actualId) {
        setLastCreatedModelId(result.actualId);
        // 삭제 폼에도 자동 입력
        setDeleteForm(prev => ({ ...prev, modelId: result.actualId! }));
      }
      
      onWorkflowComplete([result]);
    } catch (error) {
      console.error('모델 추가 테스트 실패:', error);
    }
    setLoading(false);
  }, [testRun, modelForm, onWorkflowComplete]);
  
  // === B) 가격 추가 테스트 ===
  const handleTestAddPrices = useCallback(async () => {
    if (!testRun || !lastCreatedModelId) return;
    
    setLoading(true);
    try {
      const storageOptions = modelForm.storageOptions.split(',').map(s => s.trim());
      const connectivityOptions = modelForm.deviceType === 'tablet'
        ? ['Wi-Fi', 'Wi-Fi + Cellular'] as ConnectivityType[]
        : undefined;
      
      const results = await SSOTTestService.testAddPrices(
        lastCreatedModelId,
        modelForm.deviceType,
        modelForm.brand,
        storageOptions,
        connectivityOptions
      );
      
      // 첫 번째 생성된 priceId를 수정/삭제 폼에 자동 입력
      if (results.length > 0 && results[0].actualId) {
        setPriceEditForm(prev => ({ ...prev, priceId: results[0].actualId! }));
      }
      
      onWorkflowComplete(results);
    } catch (error) {
      console.error('가격 추가 테스트 실패:', error);
    }
    setLoading(false);
  }, [testRun, lastCreatedModelId, modelForm, onWorkflowComplete]);
  
  // === C) 가격 수정 테스트 ===
  const handleTestUpdatePrice = useCallback(async () => {
    if (!testRun || !priceEditForm.priceId) return;
    
    setLoading(true);
    try {
      const newGradePrices: GradePriceInfo = {
        A: priceEditForm.gradeA,
        B: priceEditForm.gradeB,
        C: priceEditForm.gradeC,
        D: priceEditForm.gradeD,
        E: priceEditForm.gradeE
      };
      
      const result = await SSOTTestService.testUpdatePrice(
        priceEditForm.priceId,
        newGradePrices
      );
      
      onWorkflowComplete([result]);
    } catch (error) {
      console.error('가격 수정 테스트 실패:', error);
    }
    setLoading(false);
  }, [testRun, priceEditForm, onWorkflowComplete]);
  
  // === D) 가격 삭제 테스트 ===
  const handleTestDeletePrice = useCallback(async () => {
    if (!testRun || !deleteForm.modelId) return;
    
    setLoading(true);
    try {
      const result = await SSOTTestService.testDeletePrice(
        deleteForm.modelId,
        deleteForm.storage,
        deleteForm.connectivity
      );
      
      onWorkflowComplete([result]);
    } catch (error) {
      console.error('가격 삭제 테스트 실패:', error);
    }
    setLoading(false);
  }, [testRun, deleteForm, onWorkflowComplete]);
  
  // === E) Cleanup ===
  const handleCleanup = useCallback(async () => {
    if (!testRun) return;
    
    setLoading(true);
    try {
      const result = await SSOTTestService.cleanupTestData(testRun.testRunId);
      console.log('Cleanup 완료:', result);
      setLastCreatedModelId('');
      
      // 클린업 결과를 워크플로우 결과에 추가
      onWorkflowComplete([{
        testName: 'Cleanup',
        passed: true,
        expectedId: testRun.testRunId,
        actualId: null,
        details: {
          deletedModels: result.deletedModels,
          deletedPrices: result.deletedPrices
        },
        lookupSteps: []
      }]);
    } catch (error) {
      console.error('Cleanup 실패:', error);
    }
    setLoading(false);
  }, [testRun, onWorkflowComplete]);
  
  // === 조회 로그 토글 ===
  const toggleLogs = useCallback((index: number) => {
    setShowLogs(prev => ({ ...prev, [index]: !prev[index] }));
  }, []);
  
  // === 액션명 한글 변환 ===
  const getActionLabel = (action: string): string => {
    const labels: Record<string, string> = {
      'canonical_check': '정규 ID 확인',
      'alias_resolve': '별칭 변환',
      'canonical_price_lookup': '정규 가격 조회',
      'generate_model_id': '모델 ID 생성',
      'generate_price_id': '가격 ID 생성',
      'validate_canonical': '정규성 검증',
      'check_collision': '충돌 검사',
      'check_duplicate': '중복 검사',
      'save_model': '모델 저장',
      'save_price': '가격 저장',
      'verify_saved': '저장 검증',
      'update_price': '가격 수정',
      'verify_update': '수정 검증',
      'verify_lookup': '조회 검증',
      'calculate_price_id': '가격 ID 계산',
      'verify_exists': '존재 확인',
      'delete_price': '가격 삭제',
      'verify_deleted': '삭제 검증',
      'check_fallback_violation': '폴백 위반 검사',
      'alias_price_lookup': '별칭 가격 조회'
    };
    return labels[action] || action;
  };

  // === 조회 로그 렌더링 ===
  const renderLookupLogs = (logs: LookupStepLog[]) => (
    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.100' }}>
            <TableCell width={60}>단계</TableCell>
            <TableCell>작업</TableCell>
            <TableCell>입력</TableCell>
            <TableCell>출력</TableCell>
            <TableCell width={80} align="center">결과</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {logs.map((log, idx) => (
            <TableRow 
              key={idx}
              sx={{ 
                bgcolor: log.isFallback ? 'error.50' : 
                         log.success ? 'success.50' : 'transparent'
              }}
            >
              <TableCell>
                <Chip label={`#${log.step}`} size="small" variant="outlined" />
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {getActionLabel(log.action)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {log.input}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {log.output || '-'}
                </Typography>
              </TableCell>
              <TableCell align="center">
                {log.isFallback ? (
                  <Chip label="폴백" size="small" color="error" />
                ) : log.success ? (
                  <PassIcon color="success" fontSize="small" />
                ) : (
                  <FailIcon color="error" fontSize="small" />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (!testRun) {
    return (
      <Alert severity="warning">
        Workflow 테스트를 실행하려면 먼저 테스트 세션을 시작하세요.
      </Alert>
    );
  }

  return (
    <Box>
      {/* 로딩 */}
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      
      {/* 테스트 세션 정보 */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>테스트 세션:</strong> {testRun.testRunId}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          모든 쓰기 작업은 테스트 전용 컬렉션에 격리됩니다.
        </Typography>
      </Alert>
      
      {/* 마지막 생성된 모델 */}
      {lastCreatedModelId && (
        <Card variant="outlined" sx={{ mb: 2, bgcolor: 'success.50' }}>
          <CardContent sx={{ py: 1.5 }}>
            <Typography variant="caption" color="text.secondary">마지막 생성된 모델 ID</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
              {lastCreatedModelId}
            </Typography>
          </CardContent>
        </Card>
      )}
      
      {/* A) 신규 모델 추가 테스트 */}
      <Accordion expanded={expandedPanel === 'addModel'} onChange={handlePanelChange('addModel')}>
        <AccordionSummary expandIcon={<ExpandIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon color="primary" />
            <Typography fontWeight={700}>A) 신규 모델 추가 테스트</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>브랜드</InputLabel>
                <Select
                  value={modelForm.brand}
                  label="브랜드"
                  onChange={(e) => setModelForm(prev => ({ ...prev, brand: e.target.value as DeviceBrand }))}
                >
                  <MenuItem value="Apple">Apple</MenuItem>
                  <MenuItem value="Samsung">Samsung</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>디바이스 타입</InputLabel>
                <Select
                  value={modelForm.deviceType}
                  label="디바이스 타입"
                  onChange={(e) => setModelForm(prev => ({ ...prev, deviceType: e.target.value as DeviceType }))}
                >
                  <MenuItem value="smartphone">스마트폰</MenuItem>
                  <MenuItem value="tablet">태블릿</MenuItem>
                  <MenuItem value="wearable">웨어러블</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="시리즈"
                value={modelForm.series}
                onChange={(e) => setModelForm(prev => ({ ...prev, series: e.target.value }))}
                placeholder="iPhone 16"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="모델명 (영문)"
                value={modelForm.modelEn}
                onChange={(e) => setModelForm(prev => ({ ...prev, modelEn: e.target.value }))}
                placeholder="iPhone 16 Pro"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="모델명 (한글)"
                value={modelForm.modelKo}
                onChange={(e) => setModelForm(prev => ({ ...prev, modelKo: e.target.value }))}
                placeholder="아이폰 16 프로"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="저장소 옵션 (쉼표 구분)"
                value={modelForm.storageOptions}
                onChange={(e) => setModelForm(prev => ({ ...prev, storageOptions: e.target.value }))}
                placeholder="128GB,256GB,512GB"
                helperText="웨어러블은 '기본' 입력"
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={<RunIcon />}
                onClick={handleTestAddModel}
                disabled={loading}
              >
                모델 추가 테스트 실행
              </Button>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      
      {/* B) 가격 추가 테스트 */}
      <Accordion expanded={expandedPanel === 'addPrice'} onChange={handlePanelChange('addPrice')}>
        <AccordionSummary expandIcon={<ExpandIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon color="success" />
            <Typography fontWeight={700}>B) 가격 추가 테스트</Typography>
            {!lastCreatedModelId && (
              <Chip label="모델 먼저 생성" size="small" color="warning" />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {!lastCreatedModelId ? (
            <Alert severity="warning">
              먼저 "신규 모델 추가 테스트"를 실행하여 테스트 모델을 생성하세요.
            </Alert>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>대상 모델:</strong> {lastCreatedModelId}
                </Typography>
                <Typography variant="caption">
                  디바이스 타입에 따라 자동으로 variant 조합이 생성됩니다.
                  <br />
                  • 태블릿: storage × (wifi/cellular)
                  <br />
                  • 스마트폰: storage만
                  <br />
                  • 웨어러블: base(1개)
                </Typography>
              </Alert>
              <Button
                variant="contained"
                color="success"
                startIcon={<RunIcon />}
                onClick={handleTestAddPrices}
                disabled={loading}
              >
                가격 추가 테스트 실행
              </Button>
            </>
          )}
        </AccordionDetails>
      </Accordion>
      
      {/* C) 가격 수정 테스트 */}
      <Accordion expanded={expandedPanel === 'updatePrice'} onChange={handlePanelChange('updatePrice')}>
        <AccordionSummary expandIcon={<ExpandIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon color="info" />
            <Typography fontWeight={700}>C) 가격 수정 테스트</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Price ID"
                value={priceEditForm.priceId}
                onChange={(e) => setPriceEditForm(prev => ({ ...prev, priceId: e.target.value }))}
                placeholder="apple-smartphone-iphone-16-iphone-16-pro-128gb-kr"
                helperText="가격 추가 테스트 후 자동 입력됩니다"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="A등급"
                type="number"
                value={priceEditForm.gradeA}
                onChange={(e) => setPriceEditForm(prev => ({ ...prev, gradeA: Number(e.target.value) }))}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="B등급"
                type="number"
                value={priceEditForm.gradeB}
                onChange={(e) => setPriceEditForm(prev => ({ ...prev, gradeB: Number(e.target.value) }))}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="C등급"
                type="number"
                value={priceEditForm.gradeC}
                onChange={(e) => setPriceEditForm(prev => ({ ...prev, gradeC: Number(e.target.value) }))}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="D등급"
                type="number"
                value={priceEditForm.gradeD}
                onChange={(e) => setPriceEditForm(prev => ({ ...prev, gradeD: Number(e.target.value) }))}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="E등급"
                type="number"
                value={priceEditForm.gradeE}
                onChange={(e) => setPriceEditForm(prev => ({ ...prev, gradeE: Number(e.target.value) }))}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="info"
                startIcon={<RunIcon />}
                onClick={handleTestUpdatePrice}
                disabled={loading || !priceEditForm.priceId}
              >
                가격 수정 테스트 실행
              </Button>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      
      {/* D) 가격 삭제 테스트 */}
      <Accordion expanded={expandedPanel === 'deletePrice'} onChange={handlePanelChange('deletePrice')}>
        <AccordionSummary expandIcon={<ExpandIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteIcon color="error" />
            <Typography fontWeight={700}>D) 가격 삭제 테스트</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Model ID"
                value={deleteForm.modelId}
                onChange={(e) => setDeleteForm(prev => ({ ...prev, modelId: e.target.value }))}
                placeholder="apple-smartphone-iphone-16-iphone-16-pro"
                helperText="모델 추가 테스트 후 자동 입력됩니다"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Storage"
                value={deleteForm.storage}
                onChange={(e) => setDeleteForm(prev => ({ ...prev, storage: e.target.value }))}
                placeholder="128GB"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Connectivity</InputLabel>
                <Select
                  value={deleteForm.connectivity}
                  label="Connectivity"
                  onChange={(e) => setDeleteForm(prev => ({ ...prev, connectivity: e.target.value }))}
                >
                  <MenuItem value="Wi-Fi">Wi-Fi</MenuItem>
                  <MenuItem value="Wi-Fi + Cellular">Wi-Fi + Cellular</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="error"
                startIcon={<RunIcon />}
                onClick={handleTestDeletePrice}
                disabled={loading || !deleteForm.modelId}
              >
                가격 삭제 테스트 실행
              </Button>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      
      {/* E) Cleanup */}
      <Accordion expanded={expandedPanel === 'cleanup'} onChange={handlePanelChange('cleanup')}>
        <AccordionSummary expandIcon={<ExpandIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CleanupIcon color="warning" />
            <Typography fontWeight={700}>E) Cleanup (테스트 데이터 정리)</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              현재 테스트 세션 ({testRun.testRunId})에서 생성된 모든 테스트 데이터를 삭제합니다.
            </Typography>
            <Typography variant="caption">
              • 가격 → 모델 순서로 삭제
              <br />
              • 삭제 후 정합성 0 확인
            </Typography>
          </Alert>
          <Button
            variant="contained"
            color="warning"
            startIcon={<CleanupIcon />}
            onClick={handleCleanup}
            disabled={loading}
          >
            Cleanup 실행
          </Button>
        </AccordionDetails>
      </Accordion>
      
      <Divider sx={{ my: 3 }} />
      
      {/* 테스트 결과 목록 */}
      <Typography variant="h6" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <TimelineIcon />
        테스트 결과 기록 ({workflowResults.length}개)
      </Typography>
      
      {workflowResults.length === 0 ? (
        <Alert severity="info">
          아직 실행된 테스트가 없습니다.
        </Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {workflowResults.map((result, idx) => (
            <Card 
              key={idx} 
              variant="outlined"
              sx={{ 
                bgcolor: result.passed ? 'success.50' : 'error.50',
                borderColor: result.passed ? 'success.main' : 'error.main'
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {result.passed ? (
                      <PassIcon color="success" />
                    ) : (
                      <FailIcon color="error" />
                    )}
                    <Typography fontWeight={700}>
                      {result.testName}
                    </Typography>
                    <Chip 
                      label={result.passed ? 'PASS' : 'FAIL'} 
                      size="small" 
                      color={result.passed ? 'success' : 'error'}
                    />
                  </Box>
                  {result.lookupSteps.length > 0 && (
                    <Tooltip title="조회 로그 보기">
                      <IconButton size="small" onClick={() => toggleLogs(idx)}>
                        <TimelineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">예상 ID</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {result.expectedId}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">실제 ID</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {result.actualId || '-'}
                    </Typography>
                  </Grid>
                </Grid>
                
                {/* 상세 정보 표시 */}
                {result.details && (
                  <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {result.details.docIdMatch !== undefined && (
                      <Chip 
                        label={`문서 ID 일치: ${result.details.docIdMatch ? '✓' : '✗'}`} 
                        size="small" 
                        color={result.details.docIdMatch ? 'success' : 'error'}
                        variant="outlined"
                      />
                    )}
                    {result.details.hasFallback !== undefined && (
                      <Chip 
                        label={`폴백: ${result.details.hasFallback ? '발생' : '없음'}`} 
                        size="small" 
                        color={result.details.hasFallback ? 'error' : 'success'}
                        variant="outlined"
                      />
                    )}
                    {result.details.deletedModels !== undefined && (
                      <Chip 
                        label={`삭제된 모델: ${result.details.deletedModels}개`} 
                        size="small" 
                        variant="outlined"
                      />
                    )}
                    {result.details.deletedPrices !== undefined && (
                      <Chip 
                        label={`삭제된 가격: ${result.details.deletedPrices}개`} 
                        size="small" 
                        variant="outlined"
                      />
                    )}
                  </Box>
                )}
                
                {result.error && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {result.error}
                  </Alert>
                )}
                
                {/* 조회 로그 */}
                <Collapse in={showLogs[idx]}>
                  {result.lookupSteps.length > 0 && renderLookupLogs(result.lookupSteps)}
                </Collapse>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default WorkflowTab;

